/**
 * The words one exchange was spoken in, read back out of the row's `selected_option`.
 *
 * The mobile app stores each answer row as the voice call's own dialogue for that exchange —
 * `Doctor: …` / `Patient: …` lines, one per transcribed utterance — with the voice model's
 * summary of the answer appended as `[Voice model's summary]: …` (`VoiceAgent.renderConversation`).
 * The summary is what the transcript→factor mapper actually read, so it is kept, apart from the
 * bubbles. A row from before that format is a bare answer with no prefixes at all, and reads as
 * one patient utterance rather than being dropped.
 */

export type Speaker = "agent" | "patient";

export type Utterance = { speaker: Speaker; text: string };

export type TurnDialogue = {
  /** In spoken order, consecutive lines from one speaker joined into one utterance. */
  utterances: Utterance[];
  /** The voice model's summary of the answer — the mapper's input — or `null`. */
  summary: string | null;
  /** Whether the row carried the spoken dialogue (any `Doctor:` / `Patient:` line). */
  spoken: boolean;
};

const PREFIX: readonly [string, Speaker | "summary"][] = [
  ["Doctor: ", "agent"],
  ["Patient: ", "patient"],
  ["[Voice model's summary]: ", "summary"],
];

function lineKind(line: string): { kind: Speaker | "summary"; text: string } | null {
  for (const [prefix, kind] of PREFIX) {
    if (line.startsWith(prefix)) return { kind, text: line.slice(prefix.length) };
  }
  return null;
}

/** Parse a row's stored text. A blank or missing text is an exchange with no words. */
export function parseTurnDialogue(text: string | null): TurnDialogue {
  const utterances: Utterance[] = [];
  const summaryLines: string[] = [];
  let spoken = false;
  // Where an unprefixed line continues: the last utterance, or the summary once it began.
  let continuing: "utterance" | "summary" | null = null;

  for (const line of (text ?? "").split("\n")) {
    const kind = lineKind(line);
    if (kind === null) {
      if (continuing === "summary") summaryLines.push(line);
      else if (continuing === "utterance") {
        const last = utterances[utterances.length - 1];
        last.text = `${last.text}\n${line}`;
      } else if (line.trim() !== "") {
        utterances.push({ speaker: "patient", text: line });
        continuing = "utterance";
      }
      continue;
    }
    if (kind.kind === "summary") {
      summaryLines.push(kind.text);
      continuing = "summary";
      continue;
    }
    spoken = true;
    const last = utterances[utterances.length - 1];
    if (last !== undefined && last.speaker === kind.kind && continuing === "utterance") {
      last.text = `${last.text}\n${kind.text}`;
    } else {
      utterances.push({ speaker: kind.kind, text: kind.text });
    }
    continuing = "utterance";
  }

  const summary = summaryLines.join("\n").trim();
  return {
    utterances: utterances
      .map((utterance) => ({ ...utterance, text: utterance.text.trim() }))
      .filter((utterance) => utterance.text !== ""),
    summary: summary === "" ? null : summary,
    spoken,
  };
}
